import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Button } from "react-migration/components/DeprecatedButton";
import { router } from "src/js/router/react-router";
import ROUTES from "src/js/router/routes";
import { Transaction } from "../../typings/TransactionsResponse";
import { formatTransactionURL } from "../util/formatters";
import { formatDate } from "react-migration/domains/comparables/util/Dates";
import { TransactionPurchaseType } from "@gql/li-api/graphql";
import { useRoutedThirdPartyOrderQuery } from "../hooks/useRoutedThirdPartyOrderQuery";
import { LineLoading } from "./LineLoading";
import { ThirdPartyDeliverableType, ThirdPartyOrderStatus } from "@gql/sites-api/graphql";
import { SelectionType } from "src/js/stores/map/store";
import { PropsWithChildren, useCallback } from "react";
import {
  ManagementLayerTabUK,
  ManagementFilesLayerTabUK,
} from "react-migration/layouts/map/Multilayer/Bundle/Workbench/ManagmentTab/types";

type DesctiptionCellProps = {
  transaction: Transaction;
  formatDescription?: (transaction: Transaction) => string;
};

export const DescriptionCell = ({ transaction, formatDescription }: DesctiptionCellProps) => {
  const { t } = useTranslation();
  const transactionUrl = formatTransactionURL(transaction.purchase);

  const handleZoomToDocument = () => {
    const document = transaction.purchase;
    if (!document?.location?.coordinates) return;
    const coordinates = document.location.coordinates;
    router.push({
      name: ROUTES.MAP,
      params: {
        z: "19",
        x: String(coordinates[1]),
        y: String(coordinates[0]),
      },
      query: {
        sidebartype: "ownership",
        clicklatlng: JSON.stringify({
          lat: coordinates[1],
          lng: coordinates[0],
        }),
        zoom: "19",
      },
    });
  };

  if (transaction.purchase?.type === TransactionPurchaseType.ThirdPartyOnDemand) {
    return <ThirdPartyOnDemandDescriptionCell transaction={transaction} />;
  }

  return (
    <DescriptionCellWrapper>
      {transactionUrl ? (
        <a href={transactionUrl} target="_blank" rel="noreferrer">
          {formatDescription ? formatDescription(transaction) : transaction.description}
        </a>
      ) : (
        <span>{formatDescription ? formatDescription(transaction) : transaction.description}</span>
      )}
      {transaction.state.status === "pending" && (
        <span>
          {t("dashboard.transactions.status_pending")} -{" "}
          {transaction.state?.expected_response_time && (
            <>
              {t("dashboard.transactions.expected", {
                date: formatDate(transaction.state.expected_response_time),
              })}
            </>
          )}
        </span>
      )}
      {transaction.state.status === "failure" && <span>{t("dashboard.transactions.status_failure")}</span>}
      {transaction.purchase?.location && (
        <Button.Secondary size="small" className="ml-2" onClick={handleZoomToDocument}>
          {t("dashboard.transactions.view_on_map")}
        </Button.Secondary>
      )}
    </DescriptionCellWrapper>
  );
};

const ThirdPartyOnDemandDescriptionCell = ({ transaction }: DesctiptionCellProps) => {
  const { t } = useTranslation();
  const { order, loading, error } = useRoutedThirdPartyOrderQuery(transaction.purchase?.reference);
  const orderStatusDisplay = order?.status?.toLowerCase();
  const pdfDeliverable = order?.deliverables?.find((d) => d.type === ThirdPartyDeliverableType.Pdf);

  const handleViewOnMap = useCallback(() => {
    router.push({
      name: ROUTES.MAP,
      query: {
        selectionType: SelectionType.SITE,
        selectionId: order?.siteId,
        workbenchRoute: `/management/${ManagementLayerTabUK.FILES}/${ManagementFilesLayerTabUK.PURCHASED}`,
      },
    });
  }, [order?.siteId]);

  if (loading) {
    return (
      <DescriptionCellWrapper>
        <LineLoading />
      </DescriptionCellWrapper>
    );
  }

  if (error) {
    return <DescriptionCellWrapper>{transaction.description}</DescriptionCellWrapper>;
  }

  if (order?.status === ThirdPartyOrderStatus.Completed) {
    return (
      <DescriptionCellWrapper>
        <a target="_blank" rel="noreferrer" href={pdfDeliverable?.signedUrl?.url}>
          {pdfDeliverable?.filename}
        </a>
        <Button.Secondary size="small" className="ml-2" onClick={handleViewOnMap}>
          {t("dashboard.transactions.view_on_map")}
        </Button.Secondary>
      </DescriptionCellWrapper>
    );
  }

  return (
    <DescriptionCellWrapper>
      <span>{order?.provider?.displayName} Report</span>
      {orderStatusDisplay && <span className="capitalize">Status: {orderStatusDisplay}</span>}
    </DescriptionCellWrapper>
  );
};

const DescriptionCellWrapper = ({ children }: PropsWithChildren) => (
  <div className="min-w-[400px] flex justify-between items-center">{children}</div>
);
